import { useMemo, useState, useId } from "react";
import { Images } from "../../../../../../../utils/imageConstants";
import {
  formatBackendEnumLabel,
  formatPlayerRoleLabel,
  getPlayerImageUrl,
} from "../../../../../../../helper/helper";
import OverallTab from "./OverallTab";
import EachOtherTab from "./EachOtherTab";
import type { IPlayersTeamSquad, TabType } from "../types/matchDetails.d";
import type { IPlayerProfileResponse } from "../types/playerProfile";
import { useQuery } from "@tanstack/react-query";
import { Api } from "../../../../../../../services/interceptor";

interface PlayersProps {
  teamOnePlayers?: IPlayersTeamSquad;
  teamTwoPlayers?: IPlayersTeamSquad;
  matchId?: string;
  tournamentId?: string;
}


const MatchesAnalysis =({teamOnePlayers,teamTwoPlayers, matchId, tournamentId}: PlayersProps) => {
  const [isLeftOpen, setIsLeftOpen] = useState(false);
  const [isRightOpen, setIsRightOpen] = useState(false);
  const [leftPlayerId, setLeftPlayerId] = useState<string | null>(null);
  const [rightPlayerId, setRightPlayerId] = useState<string | null>(null);
  const [tabsActive, setTabsActive] = useState<TabType>("overall");
  const dropdownId = useId();
  const searchInputId = useId();
  const rightSearchInputId = useId();
  const leftPlayerOptions = useMemo(() => teamOnePlayers?.playingSquad ?? [], [teamOnePlayers]);
  const rightPlayerOptions = useMemo(() => teamTwoPlayers?.playingSquad ?? [], [teamTwoPlayers]);
  const selectedLeftPlayer = useMemo(
    () => leftPlayerOptions.find((player) => player.playerId === leftPlayerId),
    [leftPlayerId, leftPlayerOptions]
  );
  const selectedRightPlayer = useMemo(
    () => rightPlayerOptions.find((player) => player.playerId === rightPlayerId),
    [rightPlayerId, rightPlayerOptions]
  );

  const toggleLeft = () => {
    setIsLeftOpen((v) => !v);
    setIsRightOpen(false);
  };

  const toggleRight = () => {
    setIsRightOpen((v) => !v);
    setIsLeftOpen(false);
  };

  const handleTabClick = (tab: TabType) => {
    setTabsActive(tab);
  };

  const leftProfileQuery = useQuery<IPlayerProfileResponse | undefined>({
    queryKey: ["getPlayerProfile", leftPlayerId],
    enabled: !!leftPlayerId,
    queryFn: async () => {
      if (!leftPlayerId) return undefined;
      const res = await Api.get(`/getPlayerProfile/${leftPlayerId}`);
      return res?.data as IPlayerProfileResponse;
    },
  });

  const rightProfileQuery = useQuery<IPlayerProfileResponse | undefined>({
    queryKey: ["getPlayerProfile", rightPlayerId],
    enabled: !!rightPlayerId,
    queryFn: async () => {
      if (!rightPlayerId) return undefined;
      const res = await Api.get(`/getPlayerProfile/${rightPlayerId}`);
      return res?.data as IPlayerProfileResponse;
    },
  });

  const leftFacts = useMemo(() => {
    const p = leftProfileQuery.data?.playerResponse;
    if (!p) return { age: "-", batting: "-", bowling: "-", name: "" };
    const age = p.dob ? Math.max(0, Math.floor((Date.now() - new Date(p.dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000))) : "-";
    const name = `${p.playerFirstName ?? ""} ${p.playerLastName ?? ""}`.trim() || "-";
    return {
      age: String(age),
      batting: formatBackendEnumLabel(p.battingStyle),
      bowling: formatBackendEnumLabel(p.bowlingStyle),
      name,
    };
  }, [leftProfileQuery.data]);

  const rightFacts = useMemo(() => {
    const p = rightProfileQuery.data?.playerResponse;
    if (!p) return { age: "-", batting: "-", bowling: "-", name: "" };
    const age = p.dob ? Math.max(0, Math.floor((Date.now() - new Date(p.dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000))) : "-";
    const name = `${p.playerFirstName ?? ""} ${p.playerLastName ?? ""}`.trim() || "-";
    return {
      age: String(age),
      batting: formatBackendEnumLabel(p.battingStyle),
      bowling: formatBackendEnumLabel(p.bowlingStyle),
      name,
    };
  }, [rightProfileQuery.data]);

  const leftPlayerName =
    (leftFacts.name && leftFacts.name !== "-" ? leftFacts.name : undefined) ??
    [selectedLeftPlayer?.playerFirstName, selectedLeftPlayer?.playerLastName]
      .filter((part): part is string => !!part)
      .join(" ")
      .trim();
  const rightPlayerName =
    (rightFacts.name && rightFacts.name !== "-" ? rightFacts.name : undefined) ??
    [selectedRightPlayer?.playerFirstName, selectedRightPlayer?.playerLastName]
      .filter((part): part is string => !!part)
      .join(" ")
      .trim();

  const analysisPlayerFallback = Images.analysis_player_image;

  const leftPortraitSrc = useMemo(() => {
    const fromProfile = getPlayerImageUrl(leftProfileQuery.data?.playerResponse?.imageUri);
    if (fromProfile) return fromProfile;
    return getPlayerImageUrl(selectedLeftPlayer?.imageUri) ?? "";
  }, [leftProfileQuery.data?.playerResponse?.imageUri, selectedLeftPlayer?.imageUri]);

  const rightPortraitSrc = useMemo(() => {
    const fromProfile = getPlayerImageUrl(rightProfileQuery.data?.playerResponse?.imageUri);
    if (fromProfile) return fromProfile;
    return getPlayerImageUrl(selectedRightPlayer?.imageUri) ?? "";
  }, [rightProfileQuery.data?.playerResponse?.imageUri, selectedRightPlayer?.imageUri]);

  return (
    <section
      className="analysis-wrapper"
      aria-label="Players analysis and comparison"
    >
      <div className="analysis-compare-wrap">
      <div className="analysis-wrap">
        <div className="analysis-parent">
          <section
            className="player-one-group"
            aria-label="First player selection"
          >
            <figure className="player-image-parent">
              <img
                className="player-image-icon"
                src={leftPortraitSrc || analysisPlayerFallback}
                alt={leftPlayerName ? `${leftPlayerName} headshot` : "Select first player"}
                onError={(e) => {
                  const el = e.currentTarget;
                  if (el.src !== analysisPlayerFallback) {
                    el.src = analysisPlayerFallback;
                  }
                }}
              />
              <div className="add-player" onClick={toggleLeft}>
                <div className="select">{leftFacts.name || 'SELECT'}</div>
                <img className="select-icon" alt="" src={Images.select_arrow_icon} />
              </div>
              {isLeftOpen && (
                <section
                  id={dropdownId}
                  className="tournament-dropdown-points-tab"
                  role="listbox"
                  aria-label="Choose a player"
                >
                  <div className="search">
                    <input
                      id={searchInputId}
                      type="text"
                      className="search-input"
                      placeholder="Search Player"
                      aria-label="Search Player"
                    />
                    <img
                      className="frame-icon"
                      alt=""
                      aria-hidden="true"
                      src={Images.analysis_player_search_icon}
                    />
                  </div>

                  <ul className="division-list">
                    {leftPlayerOptions.map((p) => (
                      <li
                        key={p.playerId}
                        className="player-detail"
                        role="option"
                        aria-selected={leftPlayerId === p.playerId}
                        tabIndex={0}
                        onClick={() => { setLeftPlayerId(p.playerId); setIsLeftOpen(false); }}
                      >
                        <p className="player-name">{p?.playerFirstName} {p?.playerLastName}</p>
                        <span className="division-name">{formatPlayerRoleLabel(p?.playerRole)}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </figure>
            <img
              alt=""
              src={Images.analysis_cross_icon}
              className="frame-icon2"
              aria-label="Remove first player"
            />
          </section>

          {/* Middle comparison summary */}
          <section
            className="middle-wrapper"
            aria-label="Selected players summary"
          >
            <div className="selected-player-data">
              <div className="selected-players">
                <span className="player-name">{leftFacts?.name || ''}</span>
                <span className="division-name">
                  {leftProfileQuery.data?.playerResponse?.playerRole
                    ? formatPlayerRoleLabel(leftProfileQuery.data.playerResponse.playerRole)
                    : ""}
                </span>
              </div>
              <div className="selected-players">
                <span className="player-name">{rightFacts.name || ''}</span>
                <span className="division-name">
                  {rightProfileQuery.data?.playerResponse?.playerRole
                    ? formatPlayerRoleLabel(rightProfileQuery.data.playerResponse.playerRole)
                    : ""}
                </span>
              </div>
            </div>

            {/* Simple definition-like rows; keep classes */}
            <div
              className="frame-container"
              role="table"
              aria-label="Players quick facts"
            >
              <div className="header-title-parent" role="row">
                <p className="header-title" role="cell">
                  {leftFacts.age}
                </p>
                <p className="header-title2" role="cell">
                  Age
                </p>
                <p className="header-title3" role="cell">
                  {rightFacts.age}
                </p>
              </div>
              <div className="header-title-group" role="row">
                <p className="header-title" role="cell">
                  {leftFacts.batting}
                </p>
                <p className="header-title2" role="cell">
                  Batting Style
                </p>
                <p className="header-title3" role="cell">
                  {rightFacts.batting}
                </p>
              </div>
              <div className="header-title-container" role="row">
                <p className="header-title" role="cell">
                  {leftFacts.bowling}
                </p>
                <p className="header-title2" role="cell">
                  Bowling Style
                </p>
                <p className="header-title3" role="cell">
                  {rightFacts.bowling}
                </p>
              </div>
              <div className="frame-div" role="row">
                <p className="header-title" role="cell">
                  {formatPlayerRoleLabel(leftProfileQuery.data?.playerResponse?.playerRole)}
                </p>
                <p className="header-title2" role="cell">
                  Playing Role
                </p>
                <p className="header-title3" role="cell">
                  {formatPlayerRoleLabel(rightProfileQuery.data?.playerResponse?.playerRole)}
                </p>
              </div>
            </div>
          </section>

          {/* Player slot 2 */}
          <section
            className="player-one-group"
            aria-label="Second player selection"
          >
            <figure className="player-image-parent">
              <img
                className="player-image-icon"
                src={rightPortraitSrc || analysisPlayerFallback}
                alt={rightPlayerName ? `${rightPlayerName} headshot` : "Select second player"}
                onError={(e) => {
                  const el = e.currentTarget;
                  if (el.src !== analysisPlayerFallback) {
                    el.src = analysisPlayerFallback;
                  }
                }}
              />
              <div className="add-player" onClick={toggleRight}>
                <div className="select">{rightFacts.name || 'SELECT'}</div>
                <img className="select-icon" alt="" src={Images.select_arrow_icon} />
              </div>
              {isRightOpen && (
                <section
                  className="tournament-dropdown-points-tab right-player-list"
                  role="listbox"
                  aria-label="Choose a player"
                >
                  <div className="search">
                    <input
                      id={rightSearchInputId}
                      type="text"
                      className="search-input"
                      placeholder="Search Player"
                      aria-label="Search Player"
                    />
                    <img
                      className="frame-icon"
                      alt=""
                      aria-hidden="true"
                      src={Images.analysis_player_search_icon}
                    />
                  </div>
                  <ul className="division-list">
                    {rightPlayerOptions.map((p) => (
                      <li
                        key={p.playerId}
                        className="player-detail"
                        role="option"
                        aria-selected={rightPlayerId === p.playerId}
                        tabIndex={0}
                        onClick={() => { setRightPlayerId(p.playerId); setIsRightOpen(false); }}
                      >
                        <p className="player-name">{p.playerFirstName} {p.playerLastName}</p>
                        <span className="division-name">{formatPlayerRoleLabel(p.playerRole)}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </figure>
            <img
              alt=""
              src={Images.analysis_cross_icon}
              className="frame-icon4"
              aria-label="Remove second player"
            />
          </section>
        </div>
      </div>
      {/* <div className="compare-player-wrap">
        <h3>Compare Currently Trending Players</h3>
        <div className="player-chip">
          <div className="player-detail">
            <img className="player-image" alt="" src={Images.player_mini_profile} />
            <div className="player-name">Aarav Sharma</div>
          </div>
          <img className="plus-icon" alt="" src={Images.player_plus_icon} />
        </div>
      </div> */}
      </div>
      {/* Tabs */}
      <nav className="tabs-wrap" aria-label="Analysis tabs" role="tablist">
        <>
          <div
            className={`overall-tab ${
              tabsActive === "overall" ? "tab-active" : ""
            }`}
            onClick={() => handleTabClick("overall")}
          >
            <p>Overall</p>
          </div>
          <div
            className={`overall-tab ${
              tabsActive === "vsEachOther" ? "tab-active" : ""
            }`}
            onClick={() => {
              handleTabClick("vsEachOther");
              console.log("vsEcahhhhhh");
            }}
          >
            <span className="vs">vs </span>
            <span className="each-other"> Each Other</span>
          </div>
        </>
      </nav>
      <section
        id={tabsActive === "overall" ? "panel-overall" : "panel-vseachother"}
        className="tab-panel"
        role="tabpanel"
        aria-labelledby={
          tabsActive === "overall" ? "tab-overall" : "tab-vseachother"
        }
      >
        {tabsActive === "overall" ? (
          <OverallTab leftPlayerId={leftPlayerId} rightPlayerId={rightPlayerId} />
        ) : (
          <EachOtherTab
            leftPlayer={{ id: leftPlayerId, name: leftPlayerName || "Player 1" }}
            rightPlayer={{ id: rightPlayerId, name: rightPlayerName || "Player 2" }}
            matchId={matchId}
            tournamentId={tournamentId}
          />
        )}
      </section>
    </section>
  );
};

export default MatchesAnalysis;
